// src/components/Customer/WalletPayWithdrawModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import toast from "react-hot-toast";
import { FaCheck, FaTimes, FaWallet } from "react-icons/fa";
import { FiPrinter } from "react-icons/fi";
import { useGetAllBanksQuery } from "../../toolkitServices/bankApi";
import { useAddCustomerReceiptMutation } from "../../toolkitServices/customerTransactionApi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* ---------------- theme ---------------- */
const BRAND = {
  header: "#9481ce",
  primary: "#6b59c6",
  primary2: "#7b66d6",
  ring: "#7c3aed",
};

/* ---------------- react-select styling ---------------- */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 10,
    borderColor: state.isFocused ? BRAND.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `0 0 0 3px ${BRAND.ring}22` : "none",
    "&:hover": { borderColor: BRAND.ring },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
    fontWeight: 600,
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
      ? "#ddd6fe"
      : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 40 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

/* ---------------- animations ---------------- */
const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
};
const pop = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2 } },
};

/* ---------------- helpers ---------------- */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const modeOptions = [
  { value: "Cash", label: "Cash" },
  // { value: "Card", label: "Card" },
  { value: "UPI", label: "UPI" },
  { value: "Bank", label: "Bank" },
  // { value: "RTGS", label: "RTGS" },
];

const pickLedgerId = (resp) => {
  const data = resp?.data ?? resp;
  return (
    data?.ledgerId ||
    data?._id ||
    data?.id ||
    data?.transactionId ||
    data?.receiptId ||
    data?.data?.ledgerId ||
    data?.data?._id ||
    data?.data?.id ||
    null
  );
};

const pickPrintUrl = (resp) => {
  const data = resp?.data ?? resp;
  return data?.printUrl || data?.data?.printUrl || null;
};

const openReceiptPrint = ({ ledgerId, printUrl }) => {
  const url =
    printUrl ||
    (ledgerId
      ? `${API_BASE_URL}/customers/transactions/${ledgerId}/receipt-print`
      : null);

  if (!url) return toast.error("Slip print URL not found.");
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function WalletPayWithdrawModal({
  open,
  onClose,
  customerId,
  branchId,
  action = "ADD", // ADD | WITHDRAW
  customerLabel = "",
  walletBalance = null,
  onSuccess,

  // ✅ NEW (parent will pass)
  onCloseForPrintFlow, // closes main modal but keep print prompt
  onPrintFlowDone, // after print/cancel -> focus customer dropdown
  onPrintReceipt,
}) {
   const {
    data: bankResp,
    isFetching: bankLoading,
    error: bankError,
  } = useGetAllBanksQuery(
    {
      page: 1,
      limit: 200,
      search: "",
      active: true,
    })
    const banks = bankResp?.data || bankResp?.rows || [];
  
  const [addCustomerReceipt, receiptState] = useAddCustomerReceiptMutation();
  const isLoading = receiptState?.isLoading;

  const defaults = useMemo(
    () => ({
      mode: modeOptions[0],
      amount: "",
      refNo: "",
      note: "",
    }),
    []
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: defaults });

  // ✅ refs
  const modeSelectRef = useRef(null);
  const amountRef = useRef(null);

  // ✅ print prompt state (existing)
  const [printPromptOpen, setPrintPromptOpen] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  // ✅ NEW: focus Yes, Print when print prompt opens
  const yesPrintBtnRef = useRef(null);
  const selectedMode = watch("mode")?.value;

  useEffect(() => {
  setValue("refNo", "");
}, [selectedMode, setValue]);

  useEffect(() => {
    if (open) {
      reset(defaults);

      requestAnimationFrame(() => {
        if (modeSelectRef.current?.focus) modeSelectRef.current.focus();
        else amountRef.current?.focus?.();
      });
    }
  }, [open, defaults, reset]);

  // ✅ focus "Yes, Print" when print prompt opens
  useEffect(() => {
    if (printPromptOpen) {
      requestAnimationFrame(() => {
        yesPrintBtnRef.current?.focus?.();
      });
    }
  }, [printPromptOpen]);

  const title = action === "WITHDRAW" ? "Wallet Withdraw" : "Add to Wallet";
  const modeLabel = action === "WITHDRAW" ? "Payout Mode" : "Payment Mode";

  const closePrintPrompt = () => {
    setPrintPromptOpen(false);
    setLastReceipt(null);
    // ✅ after print/cancel -> focus back customer select
    onPrintFlowDone?.();
  };

  const onSubmit = async (v) => {
    if (!customerId) return toast.error("Select customer");
    if (!v.mode?.value) return toast.error("Select mode");

    const amt = num(v.amount);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");

    const bal = walletBalance == null ? null : num(walletBalance);

    // ✅ Withdraw should never exceed available; cap it.
    let finalAmt = amt;

    if (action === "WITHDRAW") {
      if (bal == null) {
        return toast.error("Wallet balance not available. Please refresh and try again.");
      }
      if (bal <= 0) {
        return toast.error("Wallet balance is 0");
      }

      if (amt > bal) {
        finalAmt = bal;
        toast.error(`Only ₹${bal.toFixed(2)} available. Withdrawing available balance.`);
        setValue("amount", String(bal), { shouldValidate: true, shouldDirty: true });
      }
    }

    try {
      const payload = {
        customerId,
        branchId: branchId || undefined,
        kind: action === "WITHDRAW" ? "WALLET_WITHDRAW" : "WALLET_ADD",
        mode: v.mode.value,
        amount: Number(finalAmt),
        refNo: v.refNo || "",
        note: v.note || "",
      };

      const resp = await addCustomerReceipt(payload).unwrap();
      toast.success(action === "WITHDRAW" ? "Withdraw saved" : "Added to wallet");

      // ✅ always refresh parent data
      onSuccess?.(resp);

      const ledgerId = pickLedgerId(resp);
      const printUrl = pickPrintUrl(resp);

      // ✅ if print not available -> just close normally (no print prompt)
      if (!ledgerId && !printUrl) {
        onClose?.();
        return;
      }

      // ✅ print prompt open (existing modal)
      setLastReceipt({ ledgerId, printUrl });
      setPrintPromptOpen(true);

      // ✅ close main Add/Withdraw modal immediately (as per requirement)
      if (onCloseForPrintFlow) onCloseForPrintFlow();
      else onClose?.();
    } catch (e) {
      const msg = e?.data?.message || e?.data?.msg || e?.error || "Something went wrong";
      toast.error(msg);
    }
  };

  const canPrintPrompt = !!(lastReceipt?.ledgerId || lastReceipt?.printUrl);

  return (
    <>
      {/* ---------------- Main Add/Withdraw Modal ---------------- */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3"
            variants={fade}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) onClose?.();
            }}
          >
            <motion.div
              className="w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
              variants={pop}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Header */}
              <div className="px-4 py-3 border-b bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce] text-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-sm font-extrabold tracking-tight">{title}</h2>
                    <p className="text-[11px] text-white/90 mt-0.5 truncate">
                      {customerLabel || customerId}
                      {branchId ? ` · Branch: ${branchId}` : ""}
                    </p>
                  <p className="text-[10px] font-bold text-red-600 mt-2 bg-red-50 border-l-4 border-red-600 px-3 py-1 rounded">
  ⚠ {action === "WITHDRAW" ? "Payment Withdrawl Details":"Payment Receiving Details"}
</p>
                    {walletBalance != null && (
                      <div className="mt-2">
                        <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold text-white border border-white/20">
                          Wallet Balance: ₹{num(walletBalance).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={onClose}
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] font-semibold hover:bg-white/15"
                    disabled={isLoading}
                  >
                    <FaTimes className="text-[10px]" />
                    Close
                  </button>
                </div>
              </div>

              {/* Body */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="p-4 space-y-3 max-h-[78vh] overflow-auto"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      {modeLabel} <span className="text-rose-500">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="mode"
                      rules={{ required: "Mode is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          ref={modeSelectRef}
                          autoFocus
                          options={modeOptions}
                          styles={selectStyles}
                          menuPortalTarget={menuPortalTarget}
                          placeholder="Select mode"
                          isClearable={false}
                          menuPlacement="auto"
                        />
                      )}
                    />
                    {errors.mode && (
                      <p className="text-red-500 text-[11px] mt-1">{errors.mode.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Amount (₹) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      ref={amountRef}
                      type="number"
                      step="0.01"
                      {...register("amount", {
                        required: "Amount is required",
                        validate: (vv) => num(vv) > 0 || "Amount must be > 0",
                      })}
                      placeholder="Enter amount"
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30"
                    />
                    {errors.amount && (
                      <p className="text-red-500 text-[11px] mt-1">{errors.amount.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
  <label className="block text-[11px] font-semibold text-slate-600 mb-1">
    {selectedMode === "Bank" ? "Select Bank" : "Ref No"}
   
  </label>

  {selectedMode === "Bank" ? (
    <div className="relative">
      <select
        {...register("refNo", {
          required:
            selectedMode === "Bank"
              ? "Bank selection is required"
              : false,
        })}
        disabled={bankLoading}
        className="w-full px-3 py-2 text-[12px] border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30 disabled:bg-gray-100"
      >
        <option value="">
          {bankLoading ? "Loading..." : "Select Bank"}
        </option>

        {!bankLoading &&
          banks?.map((b) => {
            const label = `${b?.bankName || ""} - ${b?.accountNumber || ""}`;
            return (
              <option key={b?._id} value={label}>
                {label}
              </option>
            );
          })}
      </select>

      {bankLoading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
        </span>
      )}
    </div>
  ) : (
    <input
      type="text"
      {...register("refNo")}
      placeholder="Txn / Cheque / UPI ID"
      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30"
    />
  )}

  {errors.refNo && (
    <p className="text-red-500 text-[11px] mt-1">
      {errors.refNo.message}
    </p>
  )}
    <p className="text-[10px] text-slate-700 mt-[1px] mb-1 pl-1">
    Enter the reference number {action === "WITHDRAW" ? "from where you are making the payment.":"where you are receiving the payment." }
  </p>
</div>



                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Note
                    </label>
                    <input
                      {...register("note")}
                      placeholder="Optional note"
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30"
                    />
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="pt-1 flex flex-col sm:flex-row gap-2 justify-end">
                  <motion.button
                    whileHover={{ scale: isLoading ? 1 : 1.02, y: -1 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98, y: 0 }}
                    type="submit"
                    disabled={isLoading}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-5 py-2 text-[12px] font-semibold text-white shadow-sm hover:opacity-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                      <FaCheck className="text-[10px]" />
                    </span>
                    {isLoading
                      ? "Saving..."
                      : action === "WITHDRAW"
                      ? "Confirm Withdraw"
                      : "Confirm Add"}
                  </motion.button>

                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-5 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  >
                    <FaTimes className="text-[10px]" /> Cancel
                  </button>
                </div>

               
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------------- Print Prompt Modal (existing, reused) ---------------- */}
      <AnimatePresence>
        {printPromptOpen && (
          <motion.div
            className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
            variants={fade}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closePrintPrompt();
            }}
          >
            <motion.div
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
              variants={pop}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="px-4 py-3 border-b bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce] text-white flex items-start justify-between gap-3">
                <div>
                  <div className="text-[13px] font-extrabold">Success</div>
                  <div className="text-[11px] text-white/85 mt-0.5">
                    Would you like to print receipt?
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closePrintPrompt}
                  className="h-8 w-8 rounded-xl border border-white/20 bg-white/10 hover:bg-white/15 grid place-items-center"
                  title="Close"
                  disabled={isLoading}
                >
                  <FaTimes className="text-[12px] text-white" />
                </button>
              </div>

              <div className="p-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[11px]">
                  <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">Action:</span>{" "}
                    {action === "WITHDRAW" ? "Withdraw" : "Add"}
                  </div>
                  <div className="text-slate-600 mt-1">
                    <span className="font-semibold text-slate-800">Amount:</span>{" "}
                    ₹ {Number(watch("amount") || 0).toFixed(2)}
                  </div>
                  <div className="text-slate-600 mt-1">
                    <span className="font-semibold text-slate-800">Mode:</span>{" "}
                    {watch("mode")?.value || "-"}
                  </div>
                </div>

                {!canPrintPrompt ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                    Receipt link not available from API response.
                  </div>
                ) : null}

                <div className="mt-3 flex flex-col sm:flex-row flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={closePrintPrompt}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  >
                    No, thanks
                  </button>

                  <button
                    ref={yesPrintBtnRef} // ✅ autofocus target
                    type="button"
                    disabled={isLoading || !canPrintPrompt}
                   onClick={() => {
  onPrintReceipt?.(lastReceipt?.ledgerId);
  closePrintPrompt();
}}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-4 py-2 text-[11px] font-semibold text-white hover:opacity-95 disabled:opacity-60"
                  >
                    <FiPrinter className="text-[14px]" />
                    Yes, Print
                  </button>
                </div>

                <div className="mt-2 text-[10px] text-slate-500">
                  Receipt will open in a new tab.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
